<template>
  <div class="space-y-3">
    <!-- Header Row -->
    <div class="flex items-center justify-between">
      <p class="text-xs text-muted-foreground">{{ paymentMethods.length }} card{{ paymentMethods.length !== 1 ? 's' : '' }} saved</p>
      <button
        @click="$emit('add-payment-method')"
        :disabled="isAddingCard"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cosmicPurple text-xs font-medium rounded-md text-white hover:bg-cosmicPurple/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Loader2 v-if="isAddingCard" class="w-3 h-3 animate-spin" />
        <Plus v-else class="w-3 h-3" />
        {{ isAddingCard ? 'Loading…' : 'Add Card' }}
      </button>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="isLoading" class="space-y-2">
      <div v-for="i in 2" :key="i" class="animate-pulse h-14 rounded-lg bg-gray-100"></div>
    </div>

    <!-- Empty State -->
    <div v-else-if="paymentMethods.length === 0" class="flex flex-col items-center text-center py-6 gap-2">
      <CreditCard class="w-8 h-8 text-gray-300" />
      <p class="text-sm text-muted-foreground">No payment methods added yet</p>
    </div>

    <!-- Cards List -->
    <div v-else class="space-y-2">
      <div
        v-for="paymentMethod in paymentMethods"
        :key="paymentMethod.id"
        class="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-white hover:bg-gray-50/50 transition-colors"
      >
        <div class="flex items-center gap-3">
          <!-- Brand Initial -->
          <div class="w-8 h-8 rounded-md flex items-center justify-center bg-gray-100 shrink-0 text-sm font-semibold text-gray-700">
            {{ getCardBrandLabel(paymentMethod.card_brand).charAt(0).toUpperCase() }}
          </div>

          <!-- Card Info -->
          <div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-900">
                {{ getCardBrandLabel(paymentMethod.card_brand) }} ···· {{ paymentMethod.card_last4 }}
              </span>
              <span
                v-if="paymentMethod.is_default"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 ring-1 ring-green-200"
              >
                Default
              </span>
            </div>
            <p class="text-xs text-muted-foreground">
              Expires {{ paymentMethod.card_exp_month.toString().padStart(2, '0') }}/{{ paymentMethod.card_exp_year }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-1">
          <button
            v-if="!paymentMethod.is_default"
            @click="setAsDefault(paymentMethod)"
            :disabled="isUpdating"
            class="px-2.5 py-1 text-xs text-cosmicPurple hover:bg-cosmicPurple/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Set Default
          </button>
          <button
            v-if="canDeleteCard"
            @click="openDeleteModal(paymentMethod)"
            :disabled="isUpdating"
            class="p-1.5 text-muted-foreground hover:text-destructive hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 class="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <DeleteConfirmationModal
    :visible="Boolean(pendingDelete)"
    :card-last4="pendingDelete?.card_last4"
    :loading="isUpdating"
    @close="cancelDelete"
    @confirm="confirmDelete"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import {
  Plus,
  CreditCard,
  Trash2,
  Loader2,
} from 'lucide-vue-next'
import { usePaymentMethodsStore } from '@/stores/subscription'
import { usePaymentMethodActions } from '@/composables/usePaymentMethodActions'
import { CARD_BRAND_LABELS } from '@/types/subscription'
import type { PaymentMethod } from '@/types/subscription'
import DeleteConfirmationModal from '@/components/subscription/PaymentMethod/DeleteConfirmationModal.vue'

interface Props {
  paymentMethods: PaymentMethod[]
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
})

const emit = defineEmits<{
  'add-payment-method': []
  'payment-method-updated': []
  'default-set-success': []
  'error': [message: string]
}>()

const paymentMethodsStore = usePaymentMethodsStore()
const { isAddingPaymentMethod } = storeToRefs(paymentMethodsStore)

const {
  isUpdating,
  setAsDefault: setAsDefaultAction,
  deletePaymentMethod: deletePaymentMethodAction
} = usePaymentMethodActions()

// State
const pendingDelete = ref<PaymentMethod | null>(null)

// Computed
const isAddingCard = computed(() => isAddingPaymentMethod.value)
const canDeleteCard = computed(() => props.paymentMethods.length > 1)

// Card brand helpers
const getCardBrandLabel = (brand: string) => 
  CARD_BRAND_LABELS[brand] ?? brand.toUpperCase()


// Event handlers
const emitError = (message: string) => {
  emit('error', message)
}

const handleSetAsDefault = async (paymentMethod: PaymentMethod) => {
  try {
    await setAsDefaultAction(paymentMethod)
    emit('payment-method-updated')
    emit('default-set-success')
  } catch (err: any) {
    emitError(err?.message ?? 'Failed to set default payment method')
  }
}

// Delete modal handlers
const openDeleteModal = (paymentMethod: PaymentMethod) => {
  if (!canDeleteCard.value) return
  pendingDelete.value = paymentMethod
}

const cancelDelete = () => {
  pendingDelete.value = null
}

const confirmDelete = async () => {
  if (!pendingDelete.value) return

  try {
    await deletePaymentMethodAction(pendingDelete.value.id)
    emit('payment-method-updated')
    pendingDelete.value = null
  } catch (err: any) {
    emitError(err?.message ?? 'Failed to delete payment method')
  }
}

// Alias for template compatibility
const setAsDefault = handleSetAsDefault
</script>
